import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { arcadeApi, arcadeAuth } from '../../services/arcade';
import { RegisterGate } from '../../components/arcade/RegisterGate';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
}

export const QuizDiarioPage: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(arcadeAuth.isLoggedIn());
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [available, setAvailable] = useState(true);
  const [alreadyPlayed, setAlreadyPlayed] = useState<{ score: number } | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    arcadeApi
      .quizToday()
      .then((data) => {
        setAvailable(data.available);
        setQuestions(data.questions || []);
        if (data.attempt) setAlreadyPlayed({ score: data.attempt.score });
      })
      .catch(() => setAvailable(false))
      .finally(() => setLoading(false));
  };

  useEffect(load, [loggedIn]);

  const submit = async () => {
    setError(null);
    try {
      const payload = questions.map((_, index) => answers[index] ?? -1);
      const data = await arcadeApi.quizSubmit(payload);
      setResult(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao enviar. Tente novamente.');
    }
  };

  const allAnswered = questions.length === 5 && questions.every((_, index) => answers[index] !== undefined);

  return (
    <>
      <Helmet>
        <title>Quiz Diário de Cripto | Crypto Arcade | {SITE_NAME}</title>
        <meta
          name="description"
          content="Teste seus conhecimentos com 5 perguntas diárias sobre notícias e conceitos de criptomoedas. Jogo educativo e gratuito, com explicação de cada resposta."
        />
        <link rel="canonical" href={`${SITE_URL}/arcade/quiz-diario`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/arcade" className="hover:text-blue-600 transition">Arcade</Link>
            <span>›</span>
            <span className="text-gray-700">Quiz Diário</span>
          </nav>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">🧠 Quiz Diário</h1>
          <p className="text-gray-600 mb-6">
            5 perguntas sobre as notícias do dia e conceitos cripto. 10 NC por acerto — uma chance por dia.
          </p>

          {!loggedIn ? (
            <RegisterGate onRegistered={() => setLoggedIn(true)} />
          ) : loading ? (
            <div className="text-gray-500 text-center py-10">Carregando quiz...</div>
          ) : !available ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-600">
              O quiz de hoje ainda está sendo preparado. Volte em alguns minutos!
            </div>
          ) : alreadyPlayed && !result ? (
            <div className="bg-white rounded-xl shadow p-8 text-center">
              <p className="text-2xl mb-2">✅</p>
              <p className="font-bold text-gray-900 mb-1">Você já jogou hoje!</p>
              <p className="text-gray-600 text-sm mb-4">
                Sua pontuação: <strong>{alreadyPlayed.score}/5</strong>. Novo quiz amanhã de manhã.
              </p>
              <Link to="/arcade" className="text-blue-600 hover:underline text-sm font-semibold">
                ← Voltar ao Arcade
              </Link>
            </div>
          ) : result ? (
            <div>
              <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6 text-center">
                <p className="text-4xl mb-2">{result.score === 5 ? '🏆' : result.score >= 3 ? '🎉' : '📚'}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">{result.score}/5 acertos</p>
                <p className="text-gray-600 text-sm">
                  +{result.points} NC{result.streak?.bonus ? ` · +${result.streak.bonus} NC de sequência` : ''}
                  {result.streak?.current > 1 ? ` · 🔥 ${result.streak.current} dias seguidos` : ''}
                </p>
              </div>
              <div className="space-y-4 mb-6">
                {result.corrections.map((correction: any, index: number) => (
                  <div
                    key={index}
                    className={`bg-white rounded-xl shadow p-5 border-l-4 ${correction.correct ? 'border-green-500' : 'border-red-400'}`}
                  >
                    <p className="font-semibold text-gray-900 text-sm mb-2">
                      {correction.correct ? '✅' : '❌'} {correction.question}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">{correction.explanation}</p>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link to="/arcade" className="text-blue-600 hover:underline text-sm font-semibold">
                  ← Voltar ao Arcade
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <div className="space-y-5 mb-6">
                {questions.map((question, qIndex) => (
                  <div key={question.id} className="bg-white rounded-xl shadow p-5 md:p-6">
                    <p className="font-semibold text-gray-900 mb-3">
                      {qIndex + 1}. {question.question}
                    </p>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <button
                          key={oIndex}
                          onClick={() => setAnswers((prev) => ({ ...prev, [qIndex]: oIndex }))}
                          className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition ${
                            answers[qIndex] === oIndex
                              ? 'border-blue-500 bg-blue-50 text-blue-900 font-semibold'
                              : 'border-gray-200 hover:border-blue-300 text-gray-700'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
              <button
                onClick={submit}
                disabled={!allAnswered}
                className="w-full bg-blue-600 text-white font-semibold rounded-lg px-6 py-3 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {allAnswered ? 'Enviar respostas' : `Responda todas as perguntas (${Object.keys(answers).length}/5)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
